/**
 * gd-price-filter.js
 * Custom collection price filter driven by product.metafields.custom.godata_sale_price.
 *
 * - Each grid <li> carries data-gd-price (rendered in collection-product-grid.liquid).
 * - Filtering is done in the browser by toggling [hidden]; zero requests on page load.
 * - State lives in the URL (gd_min / gd_max) so it survives reload, back/forward and sharing.
 * - The theme's own facets / toolbar JS rebuild the URL and re-render the sidebar + grid
 *   via the Section Rendering API; we listen with a MutationObserver and re-apply.
 * - When a filter is active and too few products match on the current page, the next pages'
 *   server-rendered grid HTML is fetched (same section, same params) and appended, so the
 * - Sorting: when the shopper picks "Price, low to high / high to low" (sort_by=price-ascending|
 *   price-descending) Shopify orders by variant price. We re-order the cards on the current page
 *   by godata_sale_price so the sort matches the displayed price, preserving pagination.
 */
(function () {
  'use strict';

  var PMIN = 'gd_min';
  var PMAX = 'gd_max';
  var MAX_EXTRA_PAGES = 5;      // never fetch more than this many extra pages per filter session
  var FILL_TARGET = 12;         // keep fetching until at least this many matches are visible
  var GRID_ID = 'main-collection-product-grid';
  var CONTAINER_ID = 'CollectionProductGrid';

  var state = { min: null, max: null };
  var pageCache = {};
  var loadedPage = 1;
  var loading = false;
  var exhausted = false;
  var debounceTimer = null;
  var observerTimer = null;

  /* ---------- helpers ---------- */

  function root() { return document.getElementById('GdPriceFilter'); }
  function grid() { return document.getElementById(GRID_ID); }
  function isActive() { return state.min !== null || state.max !== null; }
  /** 'asc' | 'desc' | null — read from the theme's own sort_by URL param. */
  function sortMode() {
    var v = new URLSearchParams(location.search).get('sort_by');
    return v === 'price-ascending' ? 'asc' : v === 'price-descending' ? 'desc' : null;
  }
  /** Re-order the grid's <li> by metafield price (stable; ties keep Shopify's order). */
  function sortGrid() {
    var mode = sortMode(); var g = grid();
    if (!mode || !g) return;
    var items = Array.prototype.slice.call(g.querySelectorAll('li[data-gd-price]'));
    if (items.length < 2) return;
    var keyed = items.map(function (el, i) { return { el: el, i: i, p: num(el.getAttribute('data-gd-price')) }; });
    keyed.sort(function (a, b) {
      var pa = a.p === null ? Infinity : a.p, pb = b.p === null ? Infinity : b.p;
      if (pa !== pb) return mode === 'asc' ? pa - pb : pb - pa;
      return a.i - b.i;
    });
    var changed = keyed.some(function (k, idx) { return k.el !== items[idx]; });
    if (!changed) return;
    var frag = document.createDocumentFragment();
    keyed.forEach(function (k) { frag.appendChild(k.el); });
    g.appendChild(frag);
  }
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : null; }

  function readParams(search) {
    var p = new URLSearchParams(search);
    state.min = p.has(PMIN) ? num(p.get(PMIN)) : null;
    state.max = p.has(PMAX) ? num(p.get(PMAX)) : null;
  }

  var ceiling = 100;   // slider upper bound = highest metafield price seen in this collection (rounded up)
  var ceilingSet = false;

  /** Highest data-gd-price currently in the grid, rounded up to a friendly step. Never shrinks. */
  function updateCeiling() {
    var g = grid(); if (!g) return;
    var items = g.querySelectorAll('li[data-gd-price]');
    var top = 0;
    for (var i = 0; i < items.length; i++) { var p = num(items[i].getAttribute('data-gd-price')); if (p !== null && p > top) top = p; }
    if (state.max !== null && state.max > top) top = state.max;
    if (state.min !== null && state.min > top) top = state.min;
    var step = top > 200 ? 10 : 5;
    var rounded = Math.max(step, Math.ceil(top / step) * step);
    if (!ceilingSet || rounded > ceiling) { ceiling = rounded; ceilingSet = true; }
    var r = root(); if (!r) return;
    var rMin = r.querySelector('[data-gd-range-min]');
    var rMax = r.querySelector('[data-gd-range-max]');
    var nMin = r.querySelector('[data-gd-min]');
    var nMax = r.querySelector('[data-gd-max]');
    [rMin, rMax].forEach(function (el) { if (el) el.max = ceiling; });
    if (nMin) nMin.max = ceiling;
    if (nMax) { nMax.max = ceiling; nMax.placeholder = ceiling; }
  }

  /** Paint the grey "unselected" parts of the track (theme CSS reads --left-space / --right-space). */
  function paintTrack() {
    var r = root(); if (!r) return;
    var slide = r.querySelector('[data-gd-slide]'); if (!slide) return;
    var width = slide.clientWidth || 0;
    var lo = state.min === null ? 0 : Math.min(state.min, ceiling);
    var hi = state.max === null ? ceiling : Math.min(state.max, ceiling);
    slide.style.setProperty('--left-space', (lo / ceiling) * width + 'px');
    slide.style.setProperty('--right-space', (width - (hi / ceiling) * width) + 'px');
  }

  function syncInputs() {
    var r = root(); if (!r) return;
    var minEl = r.querySelector('[data-gd-min]');
    var maxEl = r.querySelector('[data-gd-max]');
    var rMin = r.querySelector('[data-gd-range-min]');
    var rMax = r.querySelector('[data-gd-range-max]');
    if (minEl && document.activeElement !== minEl) minEl.value = state.min === null ? '' : state.min;
    if (maxEl && document.activeElement !== maxEl) maxEl.value = state.max === null ? '' : state.max;
    if (rMin && document.activeElement !== rMin) rMin.value = state.min === null ? 0 : Math.min(state.min, ceiling);
    if (rMax && document.activeElement !== rMax) rMax.value = state.max === null ? ceiling : Math.min(state.max, ceiling);
    paintTrack();
  }

  /** Re-attach our params to whatever URL the theme just wrote, without adding history entries. */
  function writeUrl() {
    var url = new URL(location.href);
    if (state.min === null) url.searchParams.delete(PMIN); else url.searchParams.set(PMIN, state.min);
    if (state.max === null) url.searchParams.delete(PMAX); else url.searchParams.set(PMAX, state.max);
    if (url.href !== location.href) history.replaceState(history.state, '', url.href);
  }

  /* ---------- core ---------- */

  function apply() {
    var g = grid();
    var r = root();
    if (!g || !r) return;

    var sorting = sortMode() !== null;
    if (sorting) sortGrid();

    var items = g.querySelectorAll('li[data-gd-price]');
    var shown = 0, i, el, price, hide;
    var active = isActive();

    for (i = 0; i < items.length; i++) {
      el = items[i];
      price = num(el.getAttribute('data-gd-price'));
      hide = active && price !== null &&
        ((state.min !== null && price < state.min) || (state.max !== null && price > state.max));
      if (el.hidden !== hide) el.hidden = hide;
      if (!hide) shown++;
    }

    // UI feedback
    var count = r.querySelector('[data-gd-count]');
    var reset = r.querySelector('[data-gd-reset]');
    var badge = r.querySelector('[data-gd-badge]');
    if (count) count.textContent = active ? shown + ' of ' + items.length + ' loaded' : '';
    if (reset) reset.hidden = !active;
    if (badge) badge.textContent = active ? '(1)' : '';

    // Native pager is meaningless while we filter client-side; hide it (restored when cleared).
    var container = document.getElementById(CONTAINER_ID);
    if (container) {
      var pager = container.querySelector('.pagination-wrapper');
      // Hide the native pager while filtering with the custom price slider
      if (pager) pager.hidden = active;
      var empty = container.querySelector('.gd-empty-message');
      var needEmpty = active && items.length > 0 && shown === 0 && (exhausted || loadedPage > MAX_EXTRA_PAGES);
      if (needEmpty && !empty) {
        empty = document.createElement('p');
        empty.className = 'gd-empty-message';
        empty.textContent = 'No products in this price range.';
        g.parentNode.insertBefore(empty, g.nextSibling);
      } else if (!needEmpty && empty) {
        empty.parentNode.removeChild(empty);
      }
    }

    if (active && shown < FILL_TARGET) loadNextPage();
  }

  /** Fetch the next page of this exact grid section (theme params intact) and append its cards. */
  function loadNextPage() {
    if (loading || exhausted || loadedPage >= MAX_EXTRA_PAGES + 1) return;
    var g = grid(); var r = root();
    if (!g || !g.dataset.id) return;

    var url = new URL(location.href);
    url.searchParams.set('section_id', g.dataset.id);
    url.searchParams.set('page', String(loadedPage + 1));
    url.searchParams.delete(PMIN);
    url.searchParams.delete(PMAX);
    var key = url.href;

    loading = true;
    if (r) r.classList.add('gd-price-filter--loading');

    var done = function (html) {
      loading = false;
      if (r) r.classList.remove('gd-price-filter--loading');
      if (html === null) { exhausted = true; apply(); return; }
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fetchedGrid = doc.getElementById(GRID_ID);
      var items = fetchedGrid ? fetchedGrid.querySelectorAll('li[data-gd-price]') : [];
      var hasNext = !!doc.querySelector('.pagination__item--next');
      loadedPage++;
      if (!items.length) { exhausted = true; apply(); return; }
      if (!hasNext) exhausted = true;   // this was the last page
      var live = grid();
      if (!live) return;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < items.length; i++) {
        var li = items[i];
        li.classList.remove('scroll-trigger', 'animate--slide-in');
        li.removeAttribute('data-cascade');
        frag.appendChild(li);
      }
      live.appendChild(frag);
      updateCeiling();
      syncInputs();
      apply();
    };

    if (pageCache[key]) { done(pageCache[key]); return; }

    fetch(key, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : null; })
      .then(function (html) { if (html !== null) pageCache[key] = html; done(html); })
      .catch(function () { done(null); });
  }

  function resetPaging() { loadedPage = 1; exhausted = false; }

  /* ---------- events (delegated: the sidebar is re-rendered by the theme) ---------- */

  function commitFromInputs() {
    var r = root(); if (!r) return;
    var minEl = r.querySelector('[data-gd-min]');
    var maxEl = r.querySelector('[data-gd-max]');
    var lo = minEl && minEl.value !== '' ? num(minEl.value) : null;
    var hi = maxEl && maxEl.value !== '' ? num(maxEl.value) : null;
    if (lo !== null && hi !== null && lo > hi) { var t = lo; lo = hi; hi = t; }
    state.min = lo !== null && lo <= 0 ? null : lo;          // 0 == no lower bound
    state.max = hi;
    writeUrl();
    syncInputs();
    apply();
  }

  function onInput(e) {
    var t = e.target;
    if (!t) return;
    var isNumber = t.hasAttribute('data-gd-min') || t.hasAttribute('data-gd-max');
    var isRange = t.hasAttribute('data-gd-range-min') || t.hasAttribute('data-gd-range-max');
    if (!isNumber && !isRange) return;
    var r = root(); if (!r) return;

    if (isRange) {
      // Mirror slider thumbs into the number boxes immediately (no filtering yet), keep thumbs ordered.
      var rMin = r.querySelector('[data-gd-range-min]');
      var rMax = r.querySelector('[data-gd-range-max]');
      var a = Number(rMin.value), b = Number(rMax.value);
      if (a > b) { if (t === rMin) { rMax.value = a; b = a; } else { rMin.value = b; a = b; } }
      r.querySelector('[data-gd-min]').value = a;
      r.querySelector('[data-gd-max]').value = b >= ceiling ? '' : b;   // thumb at far right == no max
      state.min = a <= 0 ? null : a; state.max = b >= ceiling ? null : b;
      paintTrack();
    }

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(commitFromInputs, isRange ? 150 : 300);
  }

  function onClick(e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-gd-reset]') : null;
    if (!t) return;
    e.preventDefault();
    state.min = null; state.max = null;
    var r = root();
    if (r) r.querySelectorAll('[data-gd-min],[data-gd-max]').forEach(function (i) { i.value = ''; });
    syncInputs();
    writeUrl();
    apply();
  }

  function onKeydown(e) {
    if (e.key === 'Enter' && e.target && (e.target.hasAttribute('data-gd-min') || e.target.hasAttribute('data-gd-max'))) {
      e.preventDefault();
      e.target.blur();
    }
  }

  function onPopState() {
    readParams(location.search);
    syncInputs();
    resetPaging();
    apply();
  }

  /* ---------- re-render hook ---------- */

  function observeContainer() {
    var container = document.getElementById(CONTAINER_ID);
    if (!container || !('MutationObserver' in window)) return;
    new MutationObserver(function (muts) {
      var gridReplaced = false;
      for (var i = 0; i < muts.length && !gridReplaced; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (n.id === GRID_ID || (n.querySelector && n.querySelector('#' + GRID_ID)))) { gridReplaced = true; break; }
        }
      }
      if (!gridReplaced) return;
      clearTimeout(observerTimer);
      observerTimer = setTimeout(function () {
        resetPaging();
        writeUrl();      // theme's pushState/replaceState dropped our params — put them back
        updateCeiling();
        syncInputs();
        revealUi();
        apply();
      }, 0);
    }).observe(container, { childList: true, subtree: true });
  }

  function revealUi() {
    var r = root();
    if (r && r.hidden) r.hidden = false;
  }

  /* ---------- init ---------- */

  function init() {
    if (!grid() || !root()) return;
    // The theme may already have rewritten the URL (bootstrap re-render) before we ran;
    // window.__gdInitSearch was captured inline at parse time.
    readParams(window.__gdInitSearch || location.search);
    writeUrl();
    updateCeiling();
    syncInputs();
    revealUi();
    apply();

    document.addEventListener('input', onInput);
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('popstate', onPopState);
    var resizeTimer;
    window.addEventListener('resize', function () { clearTimeout(resizeTimer); resizeTimer = setTimeout(paintTrack, 100); });
    // Track width is 0 while the sidebar is collapsed/off-canvas; repaint once it becomes visible.
    if ('ResizeObserver' in window) { var slide = root().querySelector('[data-gd-slide]'); if (slide) new ResizeObserver(paintTrack).observe(slide); }
    observeContainer();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
